import React, { useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";
import bannerBack from "../../images/banner_background.png";
import logoWhite from "../../images/logo_white.png";
import expertDefaultImg from "../../images/expert-default.png";
import industryLogo from "../../images/default_industry.png";
import scrollDesign from "../../components/EphProfile/MyProjectPage.module.css";

const IMAGE_DATABASE_URL = "https://images.expertpowerhouse.com";

function normalizeJsonArray(value) {
  if (Array.isArray(value)) return value;

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function resolveImageSource(value, fallback) {
  if (!value) return fallback;

  const source = String(value).trim();

  if (
    source.startsWith("http://") ||
    source.startsWith("https://") ||
    source.startsWith("data:image") ||
    source.startsWith("blob:")
  ) {
    return source;
  }

  return source.startsWith("/")
    ? `${IMAGE_DATABASE_URL}${source}`
    : `${IMAGE_DATABASE_URL}/${source}`;
}

function parseRelevantProjects(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }

  const html = String(value || "").trim();

  if (!html) return [];

  if (typeof DOMParser !== "undefined") {
    const parsedDocument = new DOMParser().parseFromString(
      html,
      "text/html"
    );

    const listItems = Array.from(
      parsedDocument.querySelectorAll("li")
    )
      .map((item) => item.textContent?.trim())
      .filter(Boolean);

    if (listItems.length) return listItems;

    const plainText = parsedDocument.body?.textContent?.trim();
    return plainText ? [plainText] : [];
  }

  return [];
}

function parseEducation(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }

        if (item && typeof item === "object") {
          return [
            item.degree || item.course || item.title,
            item.school || item.institution || item.university,
            [item.startDate, item.endDate]
              .filter(Boolean)
              .join(" - "),
          ]
            .filter(Boolean)
            .join("\n");
        }

        return "";
      })
      .filter(Boolean);
  }

  return String(value || "")
    .split(/\n\s*\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseRate(value) {
  const rate = String(value || "").trim();

  if (!rate) {
    return {
      currency: "",
      amount: "Not provided",
      frequency: "",
    };
  }

  const match = rate.match(
    /^([A-Za-z]{3})\s+([0-9.,]+)\s*(.*)$/i
  );

  if (!match) {
    return {
      currency: "",
      amount: rate,
      frequency: "",
    };
  }

  return {
    currency: match[1].toUpperCase(),
    amount: match[2],
    frequency: match[3] || "",
  };
}

function ReadOnlyExperience({ experience }) {
  const subExperienceBlocks = normalizeJsonArray(
    experience?.subExperienceBlocks
  );

  const logoSource = resolveImageSource(
    experience?.companyLogo,
    industryLogo
  );

  if (subExperienceBlocks.length > 0) {
    return (
      <div className="relative w-full rounded-md px-2 py-1">
        <div className="ml-1 flex items-start gap-4">
          <img
            src={logoSource}
            alt="Company Logo"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = industryLogo;
            }}
            className="h-10 w-10 rounded-full border object-contain sm:h-12 sm:w-12"
          />

          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
              {experience?.companyName || "Company"}
            </h3>
            <p className="text-sm text-gray-500">
              {experience?.duration}
            </p>
          </div>
        </div>

        <ul className="relative ml-9 mt-2 list-none pl-4">
          {subExperienceBlocks.map((role, index) => (
            <li
              key={
                role?.ID ||
                role?.id ||
                `${index}-${role?.designation || ""}`
              }
              className={`relative pl-2 ${
                index === subExperienceBlocks.length - 1
                  ? ""
                  : "mb-3"
              }`}
            >
              <div className="absolute -left-[35px] top-2 z-10 h-2 w-2 rounded-full bg-gray-400" />

              {index !== subExperienceBlocks.length - 1 && (
                <div className="absolute -left-[32px] top-5 z-0 h-full w-px bg-gray-300" />
              )}

              <h4 className="text-sm font-medium text-gray-800 sm:text-base">
                {role?.designation}
              </h4>

              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                <p className="text-xs text-gray-500">
                  {role?.joiningDate}{" "}
                  {role?.leavingDate ? "-" : null}{" "}
                  {role?.leavingDate}{" "}
                  {role?.duration ? "·" : null}{" "}
                  {role?.duration}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="relative w-full rounded-md px-2 py-1">
      <div className="ml-1 flex items-start gap-4">
        <img
          src={logoSource}
          alt="Company Logo"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = industryLogo;
          }}
          className="h-10 w-10 rounded-full border object-contain sm:h-12 sm:w-12"
        />

        <div className="flex-1 space-y-2">
          <h4 className="text-sm font-medium text-gray-800 sm:text-base">
            {experience?.designation}
          </h4>

          <p className="text-sm text-gray-500">
            {experience?.companyName}
          </p>

          <p className="text-xs text-gray-500">
            {experience?.joiningDate}{" "}
            {experience?.leavingDate ? "-" : null}{" "}
            {experience?.leavingDate}{" "}
            {experience?.duration ? "·" : null}{" "}
            {experience?.duration}
          </p>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyReviewApplication({ application }) {
  const experience = useMemo(
    () => normalizeJsonArray(application?.experience),
    [application?.experience]
  );

  const education = useMemo(
    () => parseEducation(application?.education),
    [application?.education]
  );

  const relevantProjects = useMemo(
    () => parseRelevantProjects(application?.question2),
    [application?.question2]
  );

  const rate = useMemo(
    () => parseRate(application?.expertRate),
    [application?.expertRate]
  );

  const availabilityComment =
    String(application?.availabilityComment || "").trim() ||
    "Fully available";

  const normalizedStatus = String(
    application?.status || ""
  ).toLowerCase();

  const statusMeta =
    normalizedStatus === "approved"
      ? {
          label: "Approved",
          icon: "fa-solid fa-circle-check",
          className:
            "bg-emerald-50 text-emerald-700 ring-emerald-200",
        }
      : normalizedStatus === "rejected" ||
          normalizedStatus === "declined"
        ? {
            label:
              normalizedStatus === "declined"
                ? "Declined"
                : "Rejected",
            icon: "fa-solid fa-circle-xmark",
            className:
              "bg-rose-50 text-rose-700 ring-rose-200",
          }
        : normalizedStatus === "submitted"
          ? {
              label: "Submitted",
              icon: "fa-solid fa-paper-plane",
              className:
                "bg-blue-50 text-blue-700 ring-blue-200",
            }
          : {
              label:
                application?.status ||
                "Waiting for review",
              icon: "fa-solid fa-clock",
              className:
                "bg-amber-50 text-amber-700 ring-amber-200",
            };

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Previous page/header layout */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
        <div
          className="relative h-24 bg-cover bg-center sm:h-32"
          style={{
            backgroundImage: `url(${bannerBack})`,
          }}
        >
          <div className="absolute inset-0 bg-[#173b46]/15" />

          <img
            src={logoWhite}
            alt="EPH"
            className="absolute left-4 top-4 h-auto w-20 opacity-95 sm:left-6 sm:w-24"
          />

          <span
            className={[
              "absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold ring-1 ring-inset backdrop-blur",
              statusMeta.className,
            ].join(" ")}
          >
            <i className={statusMeta.icon} />
            {statusMeta.label}
          </span>
        </div>

        <div className="relative px-4 pb-5 sm:px-6 sm:pb-6">
          <img
            src={resolveImageSource(
              application?.profilePicture,
              expertDefaultImg
            )}
            alt={application?.expertName || "Consultant"}
            className="-mt-11 h-[88px] w-[88px] rounded-full border-4 border-white bg-white object-cover shadow-lg sm:-mt-12 sm:h-24 sm:w-24"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = expertDefaultImg;
            }}
          />

          <div className="mt-3 grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-start">
            <div className="min-w-0">
              <h2 className="break-words text-2xl font-bold leading-tight text-slate-900 sm:text-3xl">
                {application?.expertName || "Consultant"}
              </h2>

              <div className="mt-3 grid text-sm text-slate-600">
                <div className="flex min-w-0 items-start gap-2">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[#365561]">
                    <i className="fa-solid fa-briefcase text-xs" />
                  </span>
                  <span className="min-w-0 break-words leading-5">
                    {application?.position ||
                      "Independent Consultant"}
                  </span>
                </div>

                <div className="flex min-w-0 items-start gap-2">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[#365561]">
                    <i className="fa-solid fa-location-dot text-xs" />
                  </span>
                  <span className="min-w-0 break-words leading-5">
                    {application?.location ||
                      "No information"}
                  </span>
                </div>

                <div className="flex min-w-0 items-start gap-2">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[#365561]">
                    <i className="fa-solid fa-comments text-xs" />
                  </span>
                  <span className="min-w-0 break-words leading-5">
                    {application?.language ||
                      "No information"}
                  </span>
                </div>

                <div className="flex min-w-0 items-start gap-2">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[#365561]">
                    <i className="fa-solid fa-calendar-days text-xs" />
                  </span>
                  <span className="min-w-0 break-words leading-5">
                    {availabilityComment}
                  </span>
                </div>
              </div>
            </div>

            {/* ReviewProfile rate design */}
            <div className="relative w-full max-w-[220px] rounded-xl bg-[#365561] px-6 py-4 text-white shadow-lg">
              <div className="text-[10px] uppercase tracking-wide opacity-80 sm:text-xs">
                rate
              </div>

              <div className="mt-1 text-xl font-bold leading-tight sm:text-3xl">
                {[rate.currency, rate.amount]
                  .filter(Boolean)
                  .join(" ")}
              </div>

              <div className="text-[10px] opacity-80 sm:text-xs">
                {rate.frequency}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ReviewProfile Summary div design */}
      <section className="relative rounded-xl bg-white p-6 shadow transition-all duration-300">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xl font-bold">EPH Summary</h2>
        </div>

        <div
          className="
            prose prose-sm text-sm leading-relaxed text-gray-700
            [&_h3]:mt-2 [&_h3]:text-md [&_h3]:font-semibold
            [&_ul]:list-disc [&_ul]:pl-5
            [&_li]:mb-1
          "
        >
          <p className="whitespace-pre-line">
            {application?.question1 ||
              "No professional summary was provided."}
          </p>

          <h3 className="mb-[2px]">Relevant project experience</h3>

          {relevantProjects.length > 0 ? (
            <ul>
              {relevantProjects.map((item, index) => (
                <li key={`${index}-${item.slice(0, 40)}`}>
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <p>
              No relevant project experience provided.
            </p>
          )}
        </div>
      </section>

      {/* ReviewProfile Experience div design */}
      <motion.section
        key="experience-view"
        layout
        initial={{ x: 0, opacity: 1 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: "100%", opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 30,
        }}
        className="relative rounded-xl bg-white p-6 shadow"
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="mb-6 text-xl font-bold">
            Experience
          </h2>
        </div>

        {experience.length > 0 ? (
          <div className="space-y-2">
            {experience.map((item, index) => (
              <ReadOnlyExperience
                key={
                  item?.ID ||
                  item?.id ||
                  `${index}-${item?.companyName || ""}`
                }
                experience={item}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No experience data available.
          </p>
        )}
      </motion.section>

      {/* ReviewProfile Education div design */}
      <section className="relative rounded-xl bg-white p-6 shadow transition-all duration-300">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xl font-bold">
            Education
          </h2>
        </div>

        {education.length > 0 ? (
          <div className="space-y-4">
            {education.map((block, index) => {
              const lines = block
                .split("\n")
                .filter(Boolean);

              return (
                <div key={`${index}-${lines[0] || ""}`}>
                  <h4 className="text-sm font-medium text-gray-800 sm:text-base">
                    {lines[0]}
                  </h4>

                  {lines.slice(1).length > 0 && (
                    <p className="text-sm text-gray-500">
                      {lines.slice(1).join(" · ")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No education data available.
          </p>
        )}
      </section>

      {/* Keep approved consent section */}
      <section className="relative rounded-xl bg-white p-6 shadow transition-all duration-300">
        <div className="mb-4 flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#365561]/10 text-[#365561]">
            <i className="fa-solid fa-microphone-lines text-sm" />
          </span>

          <h2 className="text-xl font-bold">
            Recording consent
          </h2>
        </div>

        <div
          className={[
            "flex items-center gap-3 rounded-2xl border p-4",
            application?.recordConsent
              ? "border-emerald-200 bg-emerald-50"
              : "border-slate-200 bg-slate-50",
          ].join(" ")}
        >
          <span
            className={[
              "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl",
              application?.recordConsent
                ? "bg-emerald-600 text-white"
                : "bg-slate-200 text-slate-500",
            ].join(" ")}
          >
            <i
              className={
                application?.recordConsent
                  ? "fa-solid fa-check"
                  : "fa-solid fa-xmark"
              }
            />
          </span>

          <div>
            <p className="text-sm font-bold text-slate-800">
              {application?.recordConsent
                ? "Recording consent provided"
                : "Recording consent not provided"}
            </p>

            <p className="mt-1 text-xs leading-5 text-slate-500">
              This reflects the selection made when the
              application was submitted.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function ViewApplication({
  projectID,
  expertID,
  callingFrom = "profile",
  onClose,
}) {
  const { API_URL } = useContext(UrlContext);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  useEffect(() => {
    if (!projectID) return undefined;

    const controller = new AbortController();

    const loadApplication = async () => {
      const token = localStorage.getItem("AccessToken");
      setLoading(true);

      try {
        const response = await fetch(
          `${API_URL}get-project-application`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              projectID,
              expertID,
            }),
            signal: controller.signal,
          }
        );

        const responseData = await response
          .json()
          .catch(() => ({}));

        if (
          !response.ok ||
          responseData.status !== "success"
        ) {
          throw new Error(
            responseData.message ||
              responseData.text ||
              "Failed to load application."
          );
        }

        setApplication(
          responseData.applicationData || null
        );
      } catch (error) {
        if (error.name === "AbortError") return;

        Swal.fire({
          icon: "error",
          title: "Failed to load application",
          text: error.message,
          confirmButtonColor: "#365561",
        });
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadApplication();

    return () => controller.abort();
  }, [API_URL, projectID, expertID]);

  if (!projectID) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="view-application-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-[2px]"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose?.();
          }
        }}
      >
        <motion.aside
          key="view-application-panel"
          initial={{ x: "100%", opacity: 0.75 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0.75 }}
          transition={{
            type: "spring",
            stiffness: 230,
            damping: 25,
          }}
          className={[
            scrollDesign.scrollbarOverlay,
            "relative flex h-full w-full flex-col overflow-hidden bg-[linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[-24px_0_70px_rgba(15,23,42,0.2)] sm:max-w-4xl",
          ].join(" ")}
          role="dialog"
          aria-modal="true"
          aria-label="View submitted application"
        >
          {/* Restore previous panel header */}
          <header className="shrink-0 border-b border-slate-200/80 bg-white/90 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
            <div className="flex min-w-0 items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-[#365561] sm:text-xs">
                  Submitted application
                </p>

                <h1 className="mt-1 truncate text-lg font-bold text-slate-900 sm:text-xl">
                  Application details
                </h1>

                <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                  Read-only application snapshot
                  {callingFrom === "profile"
                    ? ""
                    : ` · ${callingFrom}`}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-red-500 focus:outline-none"
                aria-label="Close application"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          </header>

          {/* Restore previous stacked scroll layout */}
          <div
            className={[
              scrollDesign.scrollbarOverlay,
              "min-h-0 flex-1 overflow-y-auto p-3 sm:p-5",
            ].join(" ")}
          >
            {loading ? (
              <div className="flex min-h-full items-center justify-center py-20">
                <div className="text-center text-[#365561]">
                  <i className="fa-solid fa-spinner fa-spin text-4xl" />

                  <p className="mt-4 text-sm font-semibold">
                    Loading application…
                  </p>
                </div>
              </div>
            ) : application ? (
              <ReadOnlyReviewApplication
                application={application}
              />
            ) : (
              <div className="flex min-h-full items-center justify-center py-20">
                <div className="max-w-sm rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-sm">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                    <i className="fa-solid fa-file-circle-xmark text-xl" />
                  </span>

                  <h2 className="mt-4 text-lg font-bold text-slate-800">
                    Application not found
                  </h2>

                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    No submitted application could be found.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Restore previous panel footer */}
          <footer className="shrink-0 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur sm:px-6">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 focus:outline-none"
              >
                Close
              </button>
            </div>
          </footer>
        </motion.aside>
      </motion.div>
    </AnimatePresence>
  );
}
